import { useState } from 'react'
import { NumberInput } from '@mantine/core'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   BUDGET_PRESETS,
   clampBudget,
   conversationsFor,
   MAX_BUDGET,
   MIN_BUDGET,
   splitBudget,
} from '../budget-presets'
import { DEV_DUMMY, useDevTools } from '../dev/devTools'
import { Card, LinkButton, OptionTile, StepFooter, StepHeader } from '../shell/Card'
import { seatCostFor } from './you-and-team/seats'

/**
 * Step 3, first screen: the monthly call budget, on its own.
 *
 * Three presets with the number of leads each one lets us work, and a
 * custom amount. The amount feeds the same `pricingData` the payment screen
 * charges from.
 */
export const Budget = () => {
   const { onboardingData, setPricingData, getNextStep, getPreviousStep } =
      useOnboardingStoreV2()
   const { pricingData } = onboardingData
   const maxSpend = pricingData?.maxSpend ?? 150
   const presetFor = BUDGET_PRESETS.find((p) => p.maxSpend === maxSpend)
   const [custom, setCustom] = useState(!presetFor)
   const seats = pricingData?.seatsCount || 1

   const applyBudget = (amount: number) =>
      setPricingData({ ...pricingData, ...splitBudget(clampBudget(amount)) })

   useDevTools({
      fill: () => applyBudget(DEV_DUMMY.maxSpend),
      skip: () => {
         getNextStep()
         return true
      },
   })

   const leads = conversationsFor(maxSpend)

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="How much should we spend calling your leads each month?"
               lede="This is the budget for the calls and texts themselves; your seats are separate. We only use what the leads need, unused balance rolls over, and you can change the amount any time."
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" role="radiogroup">
               {BUDGET_PRESETS.map((p) => {
                  const selected = !custom && maxSpend === p.maxSpend
                  return (
                     <OptionTile
                        key={p.id}
                        selected={selected}
                        onClick={() => {
                           setCustom(false)
                           applyBudget(p.maxSpend)
                        }}
                        testId={`budget-${p.id}`}
                     >
                        <p className="text-white/70 text-xs font-medium">{p.label}</p>
                        <p className="mt-0.5">
                           <span className="text-white font-semibold text-xl">${p.maxSpend}</span>
                           <span className="text-white/45 text-xs"> /month</span>
                        </p>
                        <p className="text-white text-sm mt-2">
                           About <b>{conversationsFor(p.maxSpend)}</b> leads we can work
                        </p>
                        <p className="text-white/40 text-xs mt-0.5">{p.blurb}</p>
                     </OptionTile>
                  )
               })}
            </div>

            {custom ? (
               <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <NumberInput
                     min={MIN_BUDGET}
                     max={MAX_BUDGET}
                     step={50}
                     value={maxSpend}
                     prefix="$"
                     label="Custom monthly budget"
                     classNames={{
                        label: 'text-white/90 font-medium mb-1.5 text-sm',
                        input: 'border border-[#B17AFF]/[0.5] rounded-lg p-2 bg-transparent text-white',
                     }}
                     onChange={(value) => applyBudget(Number(value))}
                     data-testid="custom-budget"
                  />
                  <p className="text-[#99A1AF] text-sm pb-2">
                     About <b className="text-white">{leads}</b> leads we can work each month
                  </p>
               </div>
            ) : (
               <LinkButton onClick={() => setCustom(true)}>Set a custom amount</LinkButton>
            )}

            <div
               className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm"
               data-testid="budget-summary"
            >
               <p className="text-white">
                  <b>${maxSpend}/mo</b> for calls and texts, about <b>{leads}</b> leads worked each
                  month.
               </p>
               <p className="text-white/45 text-xs mt-0.5">
                  Plus ${seatCostFor(seats)}/mo for {seats} {seats === 1 ? 'seat' : 'seats'}. New
                  leads cost a little more per call than older ones; the estimate assumes a mix.
               </p>
            </div>
         </Card>

         <StepFooter
            onBack={() => getPreviousStep()}
            action={`Continue with $${maxSpend}/mo`}
            onAction={() => getNextStep()}
            note="Next: your card. Nothing is charged until you confirm there."
            testId="continue-budget"
         />
      </div>
   )
}
